import { useEffect, useState } from "react";
import { useAppContext } from "../../../libs/app-core/AppContext";
import { MessageDialog } from "../../../libs/app-core/dialogs/MessageDialog";
import { SpwkUtilV3 } from "../../dao/SpwkUtilV3";
import { UniAnswer } from "../../dao/UniAnswer";
import { UniAnswerDAO } from "../../dao/UniAnswerDAO";
import { UniForm } from "../../dao/UniForm";
import { UniFormDAO } from "../../dao/UniFormDAO";
import { UniInputDAO } from "../../dao/UniInputDAO";
import { UniOption } from "../../dao/UniOption";
import { UniOptionDAO } from "../../dao/UniOptionDAO";
import { UniQuestion } from "../../dao/UniQuestion";
import { UniQuestionDAO } from "../../dao/UniQuestionDAO";
import { new_ErrorAlert } from "../../export_spwk/ExportManager";


/**
 * 輸入: 
 * uni_answer_guid 指定 answer
 * 
 * 輸出:
 * _UniAnswer 問卷答案(匯入時會新增空白的答案紙，裡面會標記應該使用哪個 特作共用題目 與 該做的問卷列表) 
 * _SpwkBaseForm 所有問卷共用的題目 1.基本資料、2.作業經歷、5.生活習慣 ...
 * _UniFormList 所有問卷列表(已填好樹狀圖 UniForm->UniQuestion->UniOption->UniInput)
 * 
 * - 目前綁定的項目，會重新安排 Parent
 */
export function useSpwkQuestV3(uni_answer_guid: string | undefined, func_guid: string | null | undefined,
  is_v3_split: boolean, is_remove_empty_question?: boolean, is_adjust_options?: boolean, IsJudgeGender?: boolean) {

  const ctx = useAppContext();

  /* 回傳: 答案卷 Answer (內涵要做哪些特作) */
  const [_UniAnswer, set_UniAnswer] = useState<UniAnswer | null | undefined>(undefined);

  /* 回傳: 特作通用問題框架 */
  const [_SpwkBaseForm, set_SpwkBaseForm] = useState<UniForm | null | undefined>(undefined);

  /* 回傳: 所有相關的 Forms */
  const [_UniFormList, set_UniFormList] = useState<UniForm[] | undefined>(undefined);


  async function async_func() {

    try {
      //#region 確認 uni_answer_guid 以準備好 or return;
      if (uni_answer_guid === undefined) {
        /* 變數尚未準備好 */
        set_UniAnswer(undefined);
        set_SpwkBaseForm(undefined);
        set_UniFormList(undefined);
        return;
      }
      //#endregion

      //#region 取得 Answer
      const answer = await UniAnswerDAO.WS_FetchByGuid(uni_answer_guid);
      if (answer == null) {
        /* 查無 Answer */
        set_UniAnswer(null);
        set_SpwkBaseForm(undefined);
        set_UniFormList(undefined);
        return;
      }

      set_UniAnswer(answer);
      //#endregion

      const base_form_guid = answer.base_form_guid;// 問卷通用問題版本
      let form_guid_list = answer.getSpwkFormGuidList();// 特作問卷版本列表

      /* 先混合一起建立樹狀圖 */
      form_guid_list = [base_form_guid, ...form_guid_list];

      /* 為了減少 Select 次數，將所有相關的資料一次撈出來，之後再用 .filter() 重新分配 */
      const cache_forms = await UniFormDAO.WS_SelectForms_ByFormGuidList(form_guid_list);
      const cache_questions = await UniQuestionDAO.WS_SelectQuestions_ByFormGuidList(form_guid_list);
      const cache_options = await UniOptionDAO.WS_SelectOptions_ByFormGuidList(form_guid_list);
      const cache_inputs = await UniInputDAO.DB_SelectInputs_ByFormGuidList(form_guid_list);
      if (IsJudgeGender) {
        const modified_options = 調整性別選項(cache_options, answer);
        UniFormDAO.FillFormTree(cache_forms, cache_questions, modified_options, cache_inputs);
      } else {
        UniFormDAO.FillFormTree(cache_forms, cache_questions, cache_options, cache_inputs);
      }
      /* 填好樹狀結構 $$$ 核心方法 */
      // UniFormDAO.FillFormTree(cache_forms, cache_questions, cache_options, cache_inputs);

      /* 找出特作通用問題 */
      const find_bases_form = cache_forms.find(x => x.guid === base_form_guid) ?? null;

      // 不管怎樣，原版的問卷先撈出來
      let form_list = cache_forms.filter(x => x.guid !== base_form_guid);

      if (func_guid != null) {// 單獨開啟指定特作(DEBUG用，此時應該唯讀)
        form_list = form_list.filter(x => x.func_guid === func_guid);
      }

      if (is_v3_split) {
        重新分配選項(find_bases_form, form_list);
      }

      if (is_adjust_options) {
        調整選項內容(find_bases_form, form_list);
      }

      if (is_remove_empty_question) {
        移除沒有選項的題目(find_bases_form, form_list);
      }



      /* 設定特作基礎項 */
      set_SpwkBaseForm(find_bases_form);

      /* 設定特作Form(排除特作通用問題) */
      set_UniFormList(form_list);

    } catch (ex: any) {
      MessageDialog.Show(ctx, ex.message, "ApiError!", { colorMeaning: 'Danger' });
      ctx.NavigateToErrorPage("", "useSpwkQuest", ex.message);
    }



  };

  useEffect(() => {
    async_func();

  }, [uni_answer_guid, is_v3_split, is_remove_empty_question, is_adjust_options, IsJudgeGender]);

  return {
    /** 問卷 Answer (內涵 通用問題版本 與 特殊作業問卷版本列表) */
    _UniAnswer,

    /** 此問卷通用問題(指定某版本) */
    _SpwkBaseForm,

    /** 包含的特殊作業問卷版本列表 */
    _UniFormList
  };



}

function 調整性別選項(cache_options: UniOption[], answer: UniAnswer | null | undefined) {
  if (answer != null) {
    if (answer.member_gender !== null) {
      for (let i = 0; i < cache_options.length; i++) {
        if (answer.member_gender === 1) {
          cache_options = cache_options.filter(x => x.gender_filter === '男' || x.gender_filter === '');
        } else if (answer.member_gender === 2) {
          cache_options = cache_options.filter(x => x.gender_filter === '女' || x.gender_filter === '');
        }
      }
    } else {
      throw new_ErrorAlert('4766841' + '請確認cache_options')
    }
  }
  return cache_options
}

/**
 * 將各題目依照 v3_group 另外重新分組
 * 主要
 * - 將A01等原本合在一起的選項，拆分成個別系統
 * - 將其他的選項也歸屬到個別系統
 **/
function 重新分配選項(_SpwkBaseForm: UniForm | null | undefined, _UniFormList: UniForm[] | undefined) {

  if (_SpwkBaseForm != null && _UniFormList != null) {
    重新分配題目群組(_SpwkBaseForm, _UniFormList, SpwkUtilV3.CHAPTER_既往病史);
    重新分配題目群組(_SpwkBaseForm, _UniFormList, SpwkUtilV3.CHAPTER_自覺症狀);
  }

}

/**
 * 對所有 Form 的 既往病史 或 自覺症狀 中的選項
 * 一一提出來重新加入 BaseForm 的固定通用題目分組中 (血液系統、呼吸系統...)
 * 
 */
function 重新分配題目群組(_SpwkBaseForm: UniForm, _UniFormList: UniForm[], CHAPTER_NAME: "既往病史" | "自覺症狀") {

  const 分類列表 = _SpwkBaseForm.AllQuestions?.filter(x => x.v3_mode === 1 && x.chapter === CHAPTER_NAME) ?? [];

  //#region 準備好找不到時，都放在 "尚未定義"
  let group_not_found = 分類列表.find(x => x.v3_mode === 1 && x.question_code === '尚未定義');

  if (group_not_found == null) {
    alert("應該要有 尚未定義 !!!");
    return;
  }

  if (group_not_found.Options == null) {
    group_not_found.Options = [];
  }
  //#endregion

  for (const form of _UniFormList) {// 對所有特作問卷
    const 題目列表 = form.AllQuestions?.filter(x => x.chapter === CHAPTER_NAME) ?? [];// 指定章節的題目

    for (const 題目 of 題目列表) {// 的所有問卷題目

      const 選項列表 = 題目.Options ?? [];

      for (const 選項 of 選項列表) {// 的所有選項 --> DO 重新依照 v3_group 分配到對應群組

        let attach_group = GetAttachGroup(分類列表, 選項, group_not_found);// 決定要分在哪一組

        if (選項.OwnerQuestion?.Options != null) {
          // 移除 (FIXME 可能有效能問題)
          選項.OwnerQuestion.Options = 選項.OwnerQuestion?.Options?.filter(x => x.guid !== 選項.guid);
        }

        選項.OwnerQuestion = attach_group;// 重新指定 Onwer

        attach_group.Options?.push(選項);// 找不到的蒐集在此，實際發布後不應該發生!!!

      }
    }
  }

}

/**
 * 嘗試從 分類列表 找到此 選項 的歸屬
 * 如果找不到，則回傳 group_not_found (歸屬在找不到!)
 */
function GetAttachGroup(分類列表: UniQuestion[], 選項: UniOption, group_not_found: UniQuestion) {
  var find_group = 分類列表.find(x => x.question_code === 選項.v3_group);

  let attach_group = null;// 決定要分在哪一組
  if (find_group != null) {
    attach_group = find_group;// 重新分配群組
  } else {
    attach_group = group_not_found;// 找不到的蒐集在此，實際發布後不應該發生!!!
  }

  return attach_group;
}


function 移除沒有選項的題目(find_bases_form: UniForm | null, form_list: UniForm[]) {

  let all_form_list = [find_bases_form, ...form_list];

  for (const form of all_form_list) {
    if (form != null) {

      form.AllQuestions = form.AllQuestions?.filter(q =>
        (q.chapter !== SpwkUtilV3.CHAPTER_既往病史 && q.chapter !== SpwkUtilV3.CHAPTER_自覺症狀) || // 不是 既往病史 或 自覺症狀 的都顯示
        (q.Options?.length !== 0 || q.kind === SpwkUtilV3.Q_KIND_標題)// 標題保留，其他題目 沒選項的隱藏
      );
    }
  }

}

/**
 * - 重新排序 [無、一般項目、其他]
 * - 隱藏拆分前的父項目
 * - 排除重複的疾病
 * - 補上 無
 */
function 調整選項內容(find_bases_form: UniForm | null, form_list: UniForm[]) {

  let all_form_list = [find_bases_form, ...form_list];

  for (const form of all_form_list) {
    if (form != null) {
      for (const question of form.AllQuestions ?? []) {

        if (!(question.chapter === SpwkUtilV3.CHAPTER_既往病史 || question.chapter === SpwkUtilV3.CHAPTER_自覺症狀)) {
          continue;
        }

        if (question.kind === SpwkUtilV3.Q_KIND_標題) {
          continue;
        }

        let options = question.Options ?? [];

        options = 排除拆分前的父項目(options);

        options = 排除重複code的選項(options);

        //-----------------------------------

        let normal_options = options.filter(x => (!x.Is其他() && !x.Is無_或_以上皆無()));
        let other_options = options.filter(x => x.Is其他());
        let none_options = options.filter(x => x.Is無_或_以上皆無());

        if (none_options.length === 0 && // 沒有無的選項
          (normal_options.length + other_options.length) > 0) {// 且 有任一非無的選項
          let opt_無 = new_Option_無(question);
          none_options.push(opt_無);
        }

        let adjusted_options = [...none_options, ...normal_options, ...other_options];

        question.Options = adjusted_options;

      }
    }
  }

}

/**
 * □慢性氣管炎、肺氣腫 已被拆分為 □慢性氣管炎 □肺氣腫  
 * 原本的項目就不用再顯示
 */
function 排除拆分前的父項目(options: UniOption[]): UniOption[] {
  options = options.filter(x => x.is_split_parent === 0);
  return options;
}

function 排除重複code的選項(options: UniOption[]): UniOption[] {
  let distinct_code_list: string[] = [];
  for (const opt of options) {

    if (opt.getV3Code() === "") {
      opt.setV3Code(opt.option_code);// 合併時，找不到的代碼先以此暫代
      opt.option_title = "{???" + opt.getV3Code() + "???} " + opt.option_title;// 並標記為異常
    }

    if (!distinct_code_list.includes(opt.getV3Code())) {
      distinct_code_list.push(opt.getV3Code());
    }
  }

  let distinct_options: UniOption[] = [];
  for (const code of distinct_code_list) {
    let find_options = options.filter(x => x.getV3Code() === code);

    //#region 確認所有同 code 的選項 最大的 input 數量 (如果發現非0且數量不同，則拋出例外)
    let max_input_cnt = 0;
    for (const opt of find_options) {
      let input_len = opt.Inputs?.length ?? 0;
      if (input_len > max_input_cnt) {

        if (max_input_cnt !== 0 && input_len !== max_input_cnt) {
          throw new_ErrorAlert("436433434 目前有 input 的選項 input 數量應該一樣 !!! opt_guid:" + opt.guid);
        }

        max_input_cnt = input_len;
      }
    }
    //#endregion

    if (max_input_cnt === 0) {
      let main_option = find_options[0];// 挑一個做代表
      distinct_options.push(main_option);
      // pass;
    } else {
      let find_option_has_input = find_options.find(x => x.Inputs?.length === max_input_cnt);
      if (find_option_has_input == null) {
        throw new_ErrorAlert("468434 這裡不應該出現 undifined !!!");
      }

      distinct_options.push(find_option_has_input);

    }
  }

  return distinct_options;
}


/**
 * 針對指定題目，動態產生無，這個選項
 */
function new_Option_無(question: UniQuestion) {
  let opt_無 = new UniOption();
  opt_無.OwnerQuestion = question;

  opt_無.question_chapter = question.chapter;
  opt_無.question_code = question.question_code;
  opt_無.question_kind = question.kind;

  opt_無.v3_group = question.question_code;
  opt_無.setV3Code(question.question_code + "_無");
  opt_無.option_title = "無";

  opt_無.Inputs = [];// 無 目前沒有 Inputs

  return opt_無;
}


